"use client"

import { useCart } from "../CartContent"
import Navbar from "./Components/Navbar"
import Footer from "./Components/Footer"
import { useEffect, useState } from "react"
import DeliveryModal from "./Components/DeliveryModal"

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity } = useCart()
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"
  const [showMenuModal, setShowMenuModal] = useState(false)

  const totalPrice = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0)

  // ✅ Log cart items for debugging checkout structure
  useEffect(() => {
    console.log("🛒 Cart Items sent to checkout:", cartItems)
  }, [cartItems])

  const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0)

  const handleDecrease = (id, quantity) => {
    if (quantity > 1) {
      updateQuantity(id, quantity - 1)
    }
  }

  const handleIncrease = (id, quantity) => {
    updateQuantity(id, quantity + 1)
  }

  const handleCheckout = async () => {
    try {
      console.log("Starting checkout with items:", cartItems)

      const response = await fetch(`${API_BASE_URL}/api/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: cartItems.map((item) => ({
            id: item.id,
            itemId: item.itemId, // Original item ID from Square
            quantity: item.quantity,
            name: item.name,
            price: item.price,
            selectedModifiers: item.selectedModifiers || {},
            specialNote: item.specialNote || null,
          })),
        }),
      })

      console.log("Checkout response status:", response.status)

      if (!response.ok) {
        const errorData = await response.json()
        console.error("Checkout error response:", errorData)
        throw new Error(errorData.error || "Failed to create checkout session")
      }

      const data = await response.json()
      console.log("Checkout success data:", data)

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl
      } else {
        alert("Checkout URL not found")
      }
    } catch (error) {
      console.error("Checkout error:", error)
      alert(`There was an error during checkout: ${error.message}. Please try again.`)
    }
  }

  return (
    <div className="bg-[#F5F5DC] min-h-screen flex flex-col">
      {/* Navbar */}
      <Navbar totalQuantity={totalQuantity} />

      {/* Cart Section */}
      <section className="w-full px-4 sm:px-6 md:px-8 lg:px-16 py-12 md:py-20 bg-orange-400 flex-grow">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-black mb-8">Your Cart</h1>

          {cartItems.length === 0 ? (
            <div className="bg-white p-6 rounded-xl shadow text-center">
              <p className="text-lg md:text-xl text-black">Your cart is empty.</p>
              <button
                type="button"
                onClick={() => setShowMenuModal(true)}
                className="inline-block mt-4 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition"
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Cart Items */}
              <div className="space-y-4">
                {cartItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex flex-col sm:flex-row items-center justify-between bg-white p-4 rounded-xl shadow gap-4"
                  >
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <img
                        src={item.image || "/placeholder.svg"}
                        alt={item.name}
                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h2 className="text-lg sm:text-xl font-semibold text-black">{item.name}</h2>

                        {/* Special Note Display */}
                        {item.specialNote && (
                          <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                            <span className="font-medium text-yellow-800">Special Note:</span>
                            <span className="text-yellow-700 ml-1">{item.specialNote}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => handleDecrease(item.id, item.quantity)}
                            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 transition"
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="text-black font-medium w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => handleIncrease(item.id, item.quantity)}
                            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 transition"
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                      <div className="text-lg sm:text-xl font-semibold text-black">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        aria-label={`Remove ${item.name} from cart`}
                        className="text-red-600 hover:text-red-800 text-xl font-bold transition"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="bg-white p-6 rounded-xl shadow">
                <div className="flex justify-between items-center text-xl sm:text-2xl font-bold text-black mb-6">
                  <span>Total:</span>
                  <span>${totalPrice.toFixed(2)}</span>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full px-6 py-3 bg-black text-white text-lg font-medium rounded-lg hover:bg-gray-800 transition"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <DeliveryModal isOpen={showMenuModal} onClose={() => setShowMenuModal(false)} pickupPath="/menu" />
    </div>
  )
}

export default Cart
